import { useRouter } from 'next/router';
import { cx } from '@linaria/core';
import { Link } from 'components/Link';
import { Popover, PopoverStateReturn } from 'components/Popover';
import { Portal } from 'reakit/Portal';
import { useQueryTE } from 'utils/react-query';
import { listAppsTE } from 'api/apps';
import { errorToReactLeft, pipe } from 'utils/fp';
import { either, taskEither } from 'fp-ts';
import { CoverImageSize, constructCoverImageURL } from 'utils/coverImage';
import { Thumbnail } from 'components/Thumbnail';
import { useEffect, useState } from 'react';
import { IconCheckThin, IconPlus } from 'components/Icons';
import { useBreakpoint } from 'utils/responsive';
import { baseTheme, breakpoints } from 'styles/utils';
import { UI_STATES, reactLeftToJSX } from 'utils/uiStates/uiStates';
import { SearchInput } from 'components/ui_library/SearchInput';
import { PopoverButton } from 'components/Navbar';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { useCreateApplicationModal } from 'modules/Apps/hooks';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { SortListPopover, sortQueryParam, SortType } from './SortPopover';
import { appsList, appsListIcon, listTitle, appsListPopoverWrapper, noScroll, createAppButtonInPopover, shrinkText } from './AppsListPopover.styles';

type AppsListPopoverProps = {
  popover: PopoverStateReturn;
  className?: string;
  style?: React.CSSProperties;
  setIsPopOverDisabled?: (isDisabled: boolean) => void;
};

const SORT_TYPE_STORAGE_KEY = 'apps_list_sort_type';

const getInitialSortType = (): string => {
  if (typeof window !== 'undefined') {
    const storedSortType = sessionStorage.getItem(SORT_TYPE_STORAGE_KEY);
    if (storedSortType && Object.values(SortType).includes(storedSortType as SortType)) {
      return storedSortType;
    }
  }
  return SortType.AtoZ;
};

export const AppsListPopover: React.FC<AppsListPopoverProps> = ({ popover, className, style, setIsPopOverDisabled }) => {
  const isMobileScreen = useBreakpoint(breakpoints.down('tab'));
  const router = useRouter();
  const { track } = useDataCollection();
  const currentUserId = (router.query.userOrOrgId as string) || useCurrentUserId();
  const { appId: appIdFromURL = '' } = router.query as { appId: string };
  const [sortType, setSortType] = useState<string>(getInitialSortType);
  const [searchAppText, setSearchAppText] = useState<string>('');

  const noScrollClassName = cx(noScroll);

  const { data: allAppsE } = useQueryTE(
    ['ListAllApps', { userId: currentUserId, sortType }],
    pipe(
      listAppsTE({ userId: currentUserId, params: `per_page=500&${sortQueryParam[sortType]}` }, errorToReactLeft),
      taskEither.map((x) => x.apps),
    ),
  );

  const hidePopover = async (_userId: string, _appId: string) => {
    await router.push(`/${_userId}/${_appId}`);
    popover.hide();
  };

  // Helper functions to manipulate sidebar state via DOM
  const setSidebarAppsPopoverVisible = (isVisible: boolean) => {
    const sidebarContainer = document.querySelector('.sidebar-container') as HTMLElement | null;
    if (sidebarContainer) {
      sidebarContainer.dataset.appsPopoverVisible = String(isVisible);
    }
  };

  const { canCreateApplication, openCreateApplicationModal } = useCreateApplicationModal({
    onAppCreated: (createdApp, fromTemplate = false) => {
      router.push(`/${createdApp.userId}/${createdApp.appId}${!fromTemplate ? '?show_wizard_model_path_modal=true' : ''}`);
      track('User App Created', { context: 'regular' });
    },
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SORT_TYPE_STORAGE_KEY, sortType);
    }
  }, [sortType]);

  useEffect(() => {
    if (popover.visible && isMobileScreen && !document.body.classList.contains(noScrollClassName)) {
      document.body.classList.add(noScrollClassName);
    } else {
      document.body.classList.remove(noScrollClassName);
    }
  }, [popover.visible, isMobileScreen]);

  // Update sidebar state based on popover visibility
  useEffect(() => {
    setSidebarAppsPopoverVisible(popover.visible);
  }, [popover.visible]);

  // Cleanup: Reset sidebar state when component unmounts
  useEffect(() => {
    return () => {
      setSidebarAppsPopoverVisible(false);
    };
  }, []);

  const componentMap = {
    [UI_STATES.error]: () => {
      setIsPopOverDisabled?.(true);
      return <></>;
    },
  };

  /**
   * TODO: Update this file to use infinite query to fetch data page wise and not load all apps at once.
   */
  return (
    <Portal>
      <Popover
        preventBodyScroll
        aria-label="Apps List"
        data-testid="user-apps-list"
        popover={popover}
        tabIndex={0}
        style={style}
        className={cx(appsListPopoverWrapper, className, 'user-apps-list-popover')}
      >
        {pipe(
          allAppsE,
          either.fold(reactLeftToJSX(componentMap), (apps) => (
            <>
              <div className={listTitle}>
                <SearchInput
                  placeholder="Search applications"
                  value={searchAppText}
                  onChange={(e) => setSearchAppText(e.target.value)}
                  style={{ flex: 1 }}
                />
                <SortListPopover sortType={sortType} onSortChange={setSortType} />
              </div>
              <ul className={appsList}>
                {apps
                  .filter((app) => app?.id?.toLowerCase().includes(searchAppText?.toLowerCase() ?? ''))
                  .map((app) => (
                    <li data-testid="user-app" key={app.id}>
                      <Link
                        onClick={() => hidePopover(app.user_id, app?.id)}
                        className={cx('app-list-item', appIdFromURL === app.id && 'active')}
                        href={`/${currentUserId}/${app.id}`}
                      >
                        <Thumbnail src={app && constructCoverImageURL(app, CoverImageSize.SMALL)} fallback={app?.id} className={appsListIcon} />
                        <span className={shrinkText}>{app.id}</span>
                        {appIdFromURL === app.id && <IconCheckThin size={16} color={baseTheme.light.colors.primary} />}
                      </Link>
                    </li>
                  ))}
              </ul>
            </>
          )),
        )}
        {canCreateApplication && (
          <PopoverButton text="Create new Application" icon={IconPlus} className={createAppButtonInPopover} onClick={openCreateApplicationModal} />
        )}
      </Popover>
    </Portal>
  );
};
